// useSessionActions.ts — the sidebar's create / fork / rename / delete
// actions for sessions and workspaces.
//
// Renames splice the returned row instead of refetching, so a rename
// mid-turn doesn't reorder the list under the user's cursor. Destructive
// actions confirm through the shared Modal (DESIGN.md C-MSG-3), passed in
// as `askConfirm` (the page's ui.useConfirm()).
import React, { useState } from 'react';
import { sessionsApi, workspacesApi } from '../api';
import type { Session, Workspace } from '../api';
import { replaceById, withoutKey } from './sessionRows';
import { workspaceIdOf } from './workspaceGroups';

type Confirm = (options: { title: string; body: string; confirmLabel: string }) => Promise<boolean>;

export interface SessionActionsArgs {
  selected: Session | null;
  selectedId: string | null;
  /** The model the picker holds for the next New session. */
  newModel: string;
  askConfirm: Confirm;
  refreshSessions: () => Promise<void>;
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
  setSessions: React.Dispatch<React.SetStateAction<Session[]>>;
  setWorkspaces: React.Dispatch<React.SetStateAction<Workspace[]>>;
  setTitleDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setStreamError: (msg: string) => void;
}

export interface SessionActions {
  /** A create in flight: 'new' for New workspace, else the workspace id. */
  creating: string | null;
  forking: boolean;
  handleNewSession: (workspaceId?: string) => Promise<void>;
  handleFork: () => Promise<void>;
  handleRenameSession: (id: string, name: string) => Promise<void>;
  handleAutoTitle: (id: string) => Promise<void>;
  handleDeleteSession: (id: string) => Promise<void>;
  handleRenameWorkspace: (id: string, name: string) => Promise<void>;
  handleDeleteWorkspace: (ws: Workspace, sessionCount: number) => Promise<void>;
}

export function useSessionActions(a: SessionActionsArgs): SessionActions {
  const {
    selected, selectedId, newModel, askConfirm, refreshSessions,
    setSelectedId, setSessions, setWorkspaces, setTitleDrafts, setStreamError,
  } = a;
  const [creating, setCreating] = useState<string | null>(null);
  const [forking, setForking] = useState(false);

  // A session with a placeholder name, in `workspaceId` or — from the list
  // header — in a new workspace of its own. The server's turn-end regen
  // replaces the name once the first reply lands, so prompting for a name
  // up-front is just friction.
  const handleNewSession = async (workspaceId?: string) => {
    if (creating) return;
    // The new row can arrive over the socket before this request returns;
    // `creating` is what says the click did something in the meantime.
    setCreating(workspaceId ?? 'new');
    try {
      console.debug('[sessions] creating session', { workspaceId: workspaceId ?? '(new)', model: newModel });
      const s = await sessionsApi.create('New session', newModel || undefined, { workspaceId });
      setSelectedId(s.id);
      await refreshSessions();
    } catch (err) {
      console.error('Failed to create session:', err);
      setStreamError(`could not create the session: ${err}`);
    } finally {
      setCreating(null);
    }
  };

  // Branch the open conversation into a new session in the same workspace
  // and open it — the way to try an alternative without losing this thread.
  const handleFork = async () => {
    if (!selected || forking) return;
    setForking(true);
    try {
      const fork = await sessionsApi.fork(selected.id, `${selected.name || 'Session'} (fork)`);
      setSelectedId(fork.id);
      await refreshSessions();
    } catch (err) {
      setStreamError(`fork failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setForking(false);
    }
  };

  // Renaming flips `name_user_set` server-side, which stops the turn-end
  // regen from overwriting the name — a title the user typed outranks one
  // the model wrote.
  const handleRenameSession = async (id: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const updated = await sessionsApi.rename(id, trimmed);
      setSessions((prev) => replaceById(prev, updated));
    } catch (err) {
      console.error('Failed to rename session:', err);
      // Drop the local draft so the row snaps back to the stored name
      // rather than showing an edit that didn't land.
      setTitleDrafts((prev) => withoutKey(prev, id));
    }
  };

  // Un-pin a name the user typed, so the next turn's regen owns it again.
  const handleAutoTitle = async (id: string) => {
    try {
      const updated = await sessionsApi.autoTitle(id);
      setSessions((prev) => replaceById(prev, updated));
    } catch (err) {
      console.error('Failed to resume auto-titling:', err);
    }
  };

  const handleDeleteSession = async (id: string) => {
    const ok = await askConfirm({
      title: 'Delete Session',
      body: 'Delete this session? Its transcript goes with it. The workspace — its machine and files — stays, along with any other sessions in it.',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await sessionsApi.delete(id);
      if (selectedId === id) setSelectedId(null);
      await refreshSessions();
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const handleRenameWorkspace = async (id: string, name: string) => {
    try {
      const updated = await workspacesApi.rename(id, name);
      setWorkspaces((prev) => replaceById(prev, updated));
    } catch (err) {
      console.error('Failed to rename workspace:', err);
    }
  };

  // The destructive one: the Pod, the working tree, the installed packages
  // and every session in it.
  const handleDeleteWorkspace = async (ws: Workspace, sessionCount: number) => {
    const ok = await askConfirm({
      title: 'Delete Workspace',
      body: `Delete "${ws.name}"? Its machine, its files and installed packages, and ${
        sessionCount === 1 ? 'its one session' : `all ${sessionCount} of its sessions`
      } go with it. Your home directory is kept.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await workspacesApi.delete(ws.id);
      if (selected && workspaceIdOf(selected) === ws.id) setSelectedId(null);
      await refreshSessions();
    } catch (err) {
      console.error('Failed to delete workspace:', err);
    }
  };

  return {
    creating, forking,
    handleNewSession, handleFork, handleRenameSession, handleAutoTitle,
    handleDeleteSession, handleRenameWorkspace, handleDeleteWorkspace,
  };
}
